import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'

import type { ActivityDto, MaterialDto, ProductDto, StageKey } from '@/stores/buildingBlock'
import { useMaterialStore } from '@/stores/material'
import { useActivityStore } from '@/stores/activity'
import { useProductStore } from '@/stores/product'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { useBuildingBlockStatus } from '@/components/building-block-form/composable/useBuildingBlockStatus'
import type { MaterialEditorProps } from '@/components/building-block-form/type/type'
import { useMaterialImpact } from '@/components/building-block-form/utils/useMaterialImpact'
import {
  DEFAULT_ACTIVITY_MODE,
  DEFAULT_ACTIVITY_VALUE
} from '@/components/building-block-form/constants/buildingBlockDefaults'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'
import {
  comparisonFields,
  formatMaterialImpactFromCalculation,
  getActivitiesForMaterial,
  getIsFirstActivityForStage,
  getSameStageCount,
  isDifferentField,
  isGroupColumn,
  getComparisonDisplayValue
} from '@/components/building-block-form/utils/materialEditorUtils'

export function useMaterialEditor(props: MaterialEditorProps) {
  const materialStore = useMaterialStore()
  const activityStore = useActivityStore()
  const productStore = useProductStore()
  const confirmDeleteStore = useConfirmDeleteStore()
  const { run } = useBuildingBlockStatus()
  const { applyActivityImpact } = useMaterialImpact()
  const { items: activityItems } = storeToRefs(activityStore)

  const infoMenu = ref(false)
  const currentProduct = ref<ProductDto | null>(null)
  const hasProduct = computed(() => props.material.product != null)

  const loadCurrentProduct = async (productId: number) => {
    const cached = productStore.items.find((item) => item.id === productId)
    if (cached) {
      currentProduct.value = cached
      return
    }

    const product = await productStore.fetchItem(productId)
    if (typeof product === 'boolean') return
    productStore.addItems([product])
    currentProduct.value = product
  }

  onMounted(async () => {
    if (props.material.product) await loadCurrentProduct(props.material.product)
  })

  watch(
    () => props.material.product,
    async (newProductId, oldProductId) => {
      if (!newProductId || newProductId === oldProductId) return
      await loadCurrentProduct(newProductId)
    }
  )

  const onLifetime = useDebounceFn(async (nextLifetime: number) => {
    await run(
      () =>
        materialStore.update({
          ...props.material,
          lifetime: nextLifetime
        }),
      props.material.block
    )
  }, DEFAULT_DEBOUNCE_DELAY)

  const onName = useDebounceFn(async (nextName: string) => {
    await run(
      () => materialStore.update({ ...props.material, name: nextName }),
      props.material.block
    )
  }, DEFAULT_DEBOUNCE_DELAY)

  const onConversionFactor = useDebounceFn(async (nextConversionFactor: number) => {
    await run(
      () =>
        materialStore.update({
          ...props.material,
          conversion_factor: nextConversionFactor
        }),
      props.material.block
    )
  }, DEFAULT_DEBOUNCE_DELAY)

  const onLifetimeTyping = (event: Event) => {
    if (props.readonly) return
    const inputElement = event.target instanceof HTMLInputElement ? event.target : null
    const textValue = inputElement?.value?.trim() ?? ''
    if (textValue === '') return

    const parsedValue = Number(textValue)
    if (!Number.isNaN(parsedValue)) {
      onLifetime(parsedValue)
    }
  }

  const onConversionFactorTyping = (event: Event) => {
    if (props.readonly) return
    const inputElement = event.target instanceof HTMLInputElement ? event.target : null
    const textValue = inputElement?.value?.trim() ?? ''
    if (textValue === '') return

    const parsedValue = Number(textValue)
    if (!Number.isNaN(parsedValue)) {
      onConversionFactor(parsedValue)
    }
  }

  const onConversionFactorDirect = (value: number | null) => {
    if (props.readonly) return
    if (value != null && !Number.isNaN(value)) {
      onConversionFactor(value)
    }
  }

  const materialImpactCalculated = computed(() =>
    formatMaterialImpactFromCalculation(props.material.impact_calculation)
  )

  const visibleComparisonFields = computed(() => comparisonFields)

  const createActivity = () => {
    if (props.readonly) return
    activityStore.create({
      material: props.material.id,
      stage: null,
      mode: DEFAULT_ACTIVITY_MODE,
      value: DEFAULT_ACTIVITY_VALUE
    })
  }

  const deleteMaterial = async () => {
    if (!props.canDelete) return
    const confirmed = await confirmDeleteStore.confirm({
      name: props.material?.name ?? 'this material'
    })
    if (!confirmed) return

    await materialStore.delete(props.material)
  }

  const activitiesForMaterial = computed(() =>
    getActivitiesForMaterial(activityItems.value, props.material.id)
  )

  const sameStageCount = (stage: string | null) => {
    return getSameStageCount(activitiesForMaterial.value, stage)
  }

  const isFirstActivityForStage = (activityId: number, stage: string | null) => {
    return getIsFirstActivityForStage(activitiesForMaterial.value, activityId, stage)
  }

  const onStageChanged = async (activity: ActivityDto, newStage: StageKey | null) => {
    if (props.readonly) return
    if (!newStage) return

    const updatedActivity = await run(
      () => activityStore.update({ ...activity, stage: newStage }),
      props.material.block
    )

    applyActivityImpact(updatedActivity)
  }

  const onModeChanged = async (activity: ActivityDto) => {
    if (props.readonly) return
    if (!activity.stage) return

    const updatedActivity = await run(() => activityStore.update(activity), props.material.block)
    if (!updatedActivity || typeof updatedActivity !== 'object') return

    for (const sibling of activitiesForMaterial.value) {
      if (
        sibling.id !== activity.id &&
        sibling.stage === activity.stage &&
        sibling.mode !== activity.mode
      ) {
        const index = activityStore.items.findIndex((item) => item.id === sibling.id)
        if (index !== -1) {
          activityStore.items[index] = { ...activityStore.items[index], mode: activity.mode }
        }
      }
    }

    applyActivityImpact(updatedActivity)
  }

  const isDifferent = (field: keyof ProductDto | keyof MaterialDto) => {
    return isDifferentField(currentProduct.value, props.material, field)
  }

  return {
    materialStore,
    activityItems,
    infoMenu,
    currentProduct,
    hasProduct,
    visibleComparisonFields,
    isGroupColumn,
    isDifferent,
    materialImpactCalculated,
    onName,
    onLifetimeTyping,
    onConversionFactorTyping,
    onConversionFactorDirect,
    deleteMaterial,
    createActivity,
    isFirstActivityForStage,
    sameStageCount,
    onStageChanged,
    onModeChanged,
    getComparisonDisplayValue
  }
}
